import { Box, HStack, Pressable, Text, VStack } from 'native-base'
import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { isMobile } from 'react-device-detect'

import { MapRef, MarkerDragEvent, Popup, ViewStateChangeEvent } from '@vis.gl/react-maplibre'
import { OrderMap as SharedOrderMap, MapPin } from '@consumer/ui'

import CloseButtonSVG from '../../assets/CloseButtonSVG'
import CloseIconWhiteSVG from '../../assets/CloseIconWhiteSVG'
import PinIconWhiteSVG from '../../assets/PinIconWhiteSVG'
import PinIconWhiteSmallSVG from '../../assets/PinIconWhiteSmallSVG'
import {
  IAdjustGeopinToast,
  IEditAddressProps,
  IToggleMapModalToastProps,
  OverlayState,
} from '../../context/OverlayContext'
import { Address, AddressImage, AddressImageType, Location, Order } from '@consumer/graphql'
import { useScreenSize } from '../../utils/displayUtils'
import { logError } from '../../utils/monitorClient'
import { colors } from '../../utils/theme/configureTheme'
import { DeliveryAddress } from '../DeliveryAddress'
import { AdjustGeopin } from './AdjustGeopin'
import MapMarker, { MapMarkerProps, MarkerVariants } from './MapMarker'

export const MAP_DEFAULT_DELTA = 0.025
export const MAP_MIN_DELTA = 0.0025
export const MAP_ZIP_DELTA = 0.1
export const MAP_CITY_DELTA = 0.14
export const MAP_STATE_DELTA = 0.1

export interface ProofOfServicePhoto {
  packageId: string
  trackingId?: string
  url?: string | null
  addressPhotoUrl?: string | null
  unitPhotoUrl?: string | null
  location?: Location | null
}

interface OrderMapProps {
  order: Order
  proofOfServicePhotos?: ProofOfServicePhoto[]
  viewRatio?: number
  bottomPaddingRatio?: number
  orderMapStyles?: {
    orderMapContainer?: object
    mapContainer?: object
  }
  toggleMap?: ({ showToastAfterClose, toastProps }: IToggleMapModalToastProps) => void
  fullScreenMap?: boolean
  addInstructions?: () => void
  editAddress?: ({ clearAddressFields, locationUpdate, useLocationUpdate, toggleMap }: IEditAddressProps) => void
  userAuthed?: boolean
  acceptedTermsAndConditions?: boolean
  showMarkerPopupForAddress?: boolean
  showGeocodeCorrection?: boolean
  showMapModal?: boolean
  handleSetOrder?: (order: Order) => void
  adjustGeopinToast: IAdjustGeopinToast
  handleSetAdjustGeopinToast?: (toast: IAdjustGeopinToast) => void
  useUpdateNormalizedAddress?: boolean
  setUseUpdateNormalizedAddress?: (useUpdateNormalizedAddress: boolean) => void
}

interface IMapMarkerProps {
  coordinate: {
    latitude: number
    longitude: number
  }
  key?: string
  zIndex?: number
  variant?: string
}

interface iAddressMarker {
  latitude: number
  longitude: number
  variant?: string
}

const middleOfUSCoords = {
  latitude: 39.953214677115,
  longitude: -98.60239691872582,
}

const MemoMarker = memo(({ position, variant, zIndex, ...props }: MapMarkerProps) => (
  <MapMarker position={position} variant={variant} zIndex={zIndex} {...props} />
))

export const OrderMap = ({
  order,
  proofOfServicePhotos,
  bottomPaddingRatio,
  viewRatio = 1,
  orderMapStyles,
  toggleMap,
  fullScreenMap = false,
  addInstructions,
  editAddress,
  userAuthed,
  acceptedTermsAndConditions,
  showMarkerPopupForAddress = false,
  showMapModal = false,
  handleSetOrder,
  showGeocodeCorrection = true,
  adjustGeopinToast,
  handleSetAdjustGeopinToast,
  useUpdateNormalizedAddress,
}: OrderMapProps) => {
  const mapRef = useRef<MapRef>(null)
  const mapKey = import.meta.env.REACT_APP_LOCATION_KEY
  const mapName = import.meta.env.REACT_APP_MAP_NAME
  const mapRegion = import.meta.env.REACT_APP_MAP_REGION
  const authedUserOwnsOrder: boolean = order?.authedUserOwnsOrder ?? false
  const address: Partial<Address> | undefined = order?.address ?? undefined
  const { toggleModal } = useContext(OverlayState)
  const screenSize = useScreenSize()
  const isMobileScreen = isMobile || screenSize.width < 1000
  const placementPhoto = authedUserOwnsOrder ? getPlacementPhoto(address) : undefined

  // MAP STATE
  const [mapMarkers, setMapMarkers] = useState<IMapMarkerProps[]>([])
  // Track current zoom for AdjustGeopin — updated via onMove callback
  const [mapZoom, setMapZoom] = useState(3)
  const [initialAddressCoords, setInitialAddressCoords] = useState({
    latitude: middleOfUSCoords.latitude,
    longitude: middleOfUSCoords.longitude,
  })
  const [addressMarkerCoords, setAddressMarkerCoords] = useState<iAddressMarker>({
    latitude: middleOfUSCoords.latitude,
    longitude: middleOfUSCoords.longitude,
    variant: 'house',
  })
  // GEO PIN CORRECTION STATE
  const [showAdjustPinPopup, setShowAdjustPinPopup] = useState<boolean>(showMarkerPopupForAddress)
  const [adjustGeopin, setAdjustGeopin] = useState<boolean>(false)
  // MAP BANNER STATE
  const [showBanner, setShowBanner] = useState<boolean>(useUpdateNormalizedAddress ?? false)

  // Add a check for valid address coordinates
  const hasValidAddressCoords = useMemo(() => {
    const addressCoordsNotDefault: boolean =
      addressMarkerCoords.latitude !== middleOfUSCoords.latitude ||
      addressMarkerCoords.longitude !== middleOfUSCoords.longitude

    return addressCoordsNotDefault
  }, [addressMarkerCoords])

  const hasProofOfServicePhotosWithLocation: boolean = useMemo(() => {
    return proofOfServicePhotos?.some(p => p.location?.lat && p.location.lng) ?? false
  }, [proofOfServicePhotos])

  useEffect(() => {
    /** Fetches address coordinates and sets map marker / address state. */
    async function fetchInitialRegion() {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const markerPropsArray: IMapMarkerProps[] = useBuildMarkerPropsArray({
        order: order as Order,
        placementPhoto,
        proofOfServicePhotos,
        userAuthed,
      })
      if (markerPropsArray?.length) {
        setMapMarkers(markerPropsArray)
        const addressMarker = markerPropsArray?.find(marker => marker?.key?.includes('marker_address'))
        if (addressMarker && userAuthed) {
          setInitialAddressCoords({
            latitude: addressMarker.coordinate.latitude,
            longitude: addressMarker.coordinate.longitude,
          })
          setAddressMarkerCoords({
            latitude: addressMarker.coordinate.latitude,
            longitude: addressMarker.coordinate.longitude,
            variant: addressMarker?.variant ?? 'house',
          })
        }
      }

      // If user is no longer authenticated, refetch the address region to update the map
      if (!userAuthed && !fullScreenMap && order?.address?.location) {
        const addrRegion = await getRegionFromAddress(order?.address)
        const updatedAddressCoords = {
          latitude: addrRegion?.[0]?.latitude ?? middleOfUSCoords.latitude,
          longitude: addrRegion?.[1]?.longitude ?? middleOfUSCoords.longitude,
          variant: 'house',
          zoom: addrRegion?.[0]?.latitude && addrRegion?.[0]?.longitude ? 16 : 3,
        }
        setInitialAddressCoords(updatedAddressCoords)
        setAddressMarkerCoords({
          latitude: updatedAddressCoords.latitude,
          longitude: updatedAddressCoords.longitude,
          variant: updatedAddressCoords.variant,
        })
        setTimeout(() => {
          mapRef.current?.panTo({
            lat: updatedAddressCoords.latitude,
            lng: updatedAddressCoords.longitude,
          })
        }, 100)
        return
      }
    }

    fetchInitialRegion()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, userAuthed, proofOfServicePhotos])

  useEffect(() => {
    if (fullScreenMap) {
      if (useUpdateNormalizedAddress) {
        setShowBanner(true)
        setAdjustGeopin(true)
      }
    }
  }, [useUpdateNormalizedAddress, fullScreenMap])

  const handleHideMarkerPopup = useCallback(() => {
    setShowAdjustPinPopup(false)
  }, [])

  const handleSetAddressMarkerCoords = useCallback(
    (dragEvent: MarkerDragEvent) => {
      const { lngLat } = dragEvent
      setAddressMarkerCoords({ latitude: lngLat?.lat, longitude: lngLat?.lng, variant: addressMarkerCoords?.variant })
      setAdjustGeopin(true)
    },
    [addressMarkerCoords]
  )

  const handlePopupPress = useCallback(() => {
    setShowAdjustPinPopup(false)
    setAdjustGeopin(true)
  }, [])

  const handleToggleMapBanner = useCallback(() => {
    setShowBanner(!showBanner)
  }, [showBanner])

  // doesn't include address marker as that can be draggable
  const MapMarkers = useCallback(() => {
    return (
      <>
        {mapMarkers.map(marker => {
          const { latitude: lat, longitude: lng } = marker.coordinate
          return (
            !marker?.key?.includes('marker_address') && (
              <MemoMarker
                position={{ lat, lng }}
                variant={marker?.variant as MarkerVariants}
                draggable={false}
                zIndex={marker?.zIndex}
              />
            )
          )
        })}
      </>
    )
  }, [mapMarkers])

  const AddressMarker = useCallback(() => {
    const { latitude, longitude, variant } = addressMarkerCoords || {}
    const draggable = !!(
      showGeocodeCorrection &&
      showMarkerPopupForAddress &&
      order?.isOrderReroutableByConsumer &&
      fullScreenMap
    )
    const showPinPopup = !!(showGeocodeCorrection && showAdjustPinPopup && order?.isOrderReroutableByConsumer)

    return (
      <>
        <MapMarker
          position={{ lat: latitude, lng: longitude }}
          variant={variant as MarkerVariants}
          draggable={draggable}
          onClick={() => {
            if (!adjustGeopin && order?.isOrderReroutableByConsumer && fullScreenMap) {
              setAdjustGeopin(true)
              setShowBanner(true)
            }
          }}
          onDragStart={() => {
            if (fullScreenMap) {
              handleHideMarkerPopup()
            }
          }}
          onDrag={() => {
            if (fullScreenMap) {
              handleHideMarkerPopup()
            }
          }}
          onDragEnd={(dragEvent: MarkerDragEvent) => {
            if (fullScreenMap) {
              handleSetAddressMarkerCoords(dragEvent)
            }
          }}
          offset={[0, -20]}
          zIndex={3000}
        />
        {showPinPopup && (
          <Pressable onPress={handlePopupPress} style={{ zIndex: 3000, backgroundColor: 'transparent' }}>
            <Popup
              longitude={longitude}
              latitude={latitude}
              anchor="bottom"
              offset={[0, -60] as [number, number]}
              closeButton={false}
            >
              <HStack style={{ minWidth: 90, alignItems: 'center', justifyContent: 'space-between' }}>
                <Text color={colors.brand.white} fontSize={14} fontWeight={700} lineHeight={17.5}>
                  Adjust Pin
                </Text>
                <PinIconWhiteSmallSVG />
              </HStack>
            </Popup>
          </Pressable>
        )}
      </>
    )
  }, [
    addressMarkerCoords,
    showMarkerPopupForAddress,
    showAdjustPinPopup,
    handleHideMarkerPopup,
    handleSetAddressMarkerCoords,
    handlePopupPress,
    adjustGeopin,
    setAdjustGeopin,
    showGeocodeCorrection,
    order,
    fullScreenMap,
    setShowBanner,
  ])

  const handleMapOnClick = useCallback(() => {
    if (!userAuthed) {
      if (order?.instructionEditsAllowed) {
        toggleModal({ showModal: true, auth_flow: 'general' })
      }
    } else {
      if (toggleMap && !fullScreenMap) {
        toggleMap({ showToastAfterClose: false })
      }
    }
  }, [userAuthed, toggleMap, toggleModal, order?.instructionEditsAllowed, fullScreenMap])

  const resetAddressMarker = useCallback(() => {
    const { latitude, longitude } = initialAddressCoords
    const { variant } = addressMarkerCoords
    setAddressMarkerCoords({
      latitude,
      longitude,
      variant,
    })
    setShowAdjustPinPopup(showMarkerPopupForAddress)
  }, [initialAddressCoords, addressMarkerCoords, showMarkerPopupForAddress])

  const handleCloseMap = useCallback(
    ({ showToastAfterClose = false, toastProps = {} }: IToggleMapModalToastProps) => {
      setAdjustGeopin(false)
      setShowBanner(false)
      resetAddressMarker()
      if (toggleMap) toggleMap({ showToastAfterClose: showToastAfterClose, toastProps: toastProps })
      if (handleSetAdjustGeopinToast) {
        handleSetAdjustGeopinToast({
          headerText: 'Adjust Delivery Location',
          bodyText: 'Move pin on the map to update',
        })
      }
    },
    [toggleMap, resetAddressMarker, setAdjustGeopin, handleSetAdjustGeopinToast, setShowBanner]
  )

  // close full screen map if user session expires and map is open
  useEffect(() => {
    /** Close the map for unauthenticated users */
    function closeMapForUnauthedUser() {
      if (fullScreenMap && showMapModal && order?.address?.location && !userAuthed) {
        handleCloseMap({ showToastAfterClose: false })
      }
    }

    closeMapForUnauthedUser()
  }, [fullScreenMap, showMapModal, userAuthed, order, handleCloseMap])

  // pan map to address marker when full screen map is opened
  useEffect(() => {
    const panToAddressMarker = async () => {
      if (fullScreenMap && showMapModal && addressMarkerCoords?.latitude && addressMarkerCoords?.longitude) {
        const { latitude, longitude } = addressMarkerCoords
        const targetZoom = 17

        setTimeout(async () => {
          if (mapRef.current) {
            try {
              await mapRef.current.easeTo({
                center: [longitude, latitude],
                zoom: targetZoom,
                duration: 800,
                essential: true,
              })
            } catch (error) {
              logError('Error panning to address marker', { error })
            }
          }
        }, 100)
      }
    }

    panToAddressMarker()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullScreenMap, showMapModal])

  // Allow ESC key to close full screen map
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (fullScreenMap && showMapModal && event.key === 'Escape') {
        handleCloseMap({ showToastAfterClose: false })
      }
    }

    if (fullScreenMap && showMapModal) {
      window.addEventListener('keydown', handleKeyPress)
    }

    return () => {
      window.removeEventListener('keydown', handleKeyPress)
    }
  }, [fullScreenMap, handleCloseMap, showMapModal])

  const handleToggleEditAddress = useCallback(
    ({ clearAddressFields, locationUpdate, useLocationUpdate }: IEditAddressProps) => {
      if (editAddress) {
        editAddress({ clearAddressFields, locationUpdate, useLocationUpdate, toggleMap: true })
      }
    },
    [editAddress]
  )

  const handleMapMove = useCallback((evt: ViewStateChangeEvent) => {
    setMapZoom(evt.viewState.zoom)
  }, [])

  /**
   * Calculate the right percentage based on the banner body text length
   * @param textLength the length of the banner body text
   * @returns the calculated right percentage
   */
  const calculateOffset = useCallback((textLength: number): number => {
    const shortTextPercentage = 35
    const longTextPercentage = 25
    const minPercentage = 20

    type TextRange = {
      min: number
      max: number
    } & ({ percentage: number } | { startPercentage: number; endPercentage: number })

    const textRanges: TextRange[] = [
      { min: 0, max: 29, percentage: shortTextPercentage },
      { min: 29, max: 68, startPercentage: shortTextPercentage, endPercentage: longTextPercentage },
      { min: 68, max: Infinity, percentage: longTextPercentage },
    ]

    const range = textRanges.find(r => textLength > r.min && textLength <= r.max)

    if (!range) {
      return Math.max(shortTextPercentage, minPercentage)
    }

    if ('percentage' in range) {
      return Math.max(range.percentage, minPercentage)
    }

    const progress = (textLength - range.min) / (range.max - range.min)
    const interpolatedValue = range.startPercentage + progress * (range.endPercentage - range.startPercentage)
    return Math.max(interpolatedValue, minPercentage)
  }, [])

  const MapBanner = useCallback(() => {
    const bodyTextLength =
      adjustGeopinToast?.bodyText?.length > 0
        ? adjustGeopinToast?.bodyText?.length
        : (adjustGeopinToast?.headerText?.length ?? 0)
    const calculatedRightPercentage = calculateOffset(bodyTextLength)

    return (
      <HStack
        style={{
          position: 'absolute',
          top: 10,
          zIndex: 3000,
          backgroundColor: colors.brand.black,
          borderRadius: 12,
          right: isMobile ? (useUpdateNormalizedAddress ? '0%' : '7%') : `${calculatedRightPercentage}%`,
          display: 'flex',
          alignItems: 'center',
          minWidth: 350,
          justifyContent: 'space-between',
          paddingHorizontal: 16,
          paddingVertical: 10,
          marginHorizontal: useUpdateNormalizedAddress ? 10 : 'auto',
        }}
      >
        <Box>
          <PinIconWhiteSVG />
        </Box>
        <VStack
          style={{
            display: 'flex',
            justifyContent: 'center',
            width: useUpdateNormalizedAddress ? '80%' : 'auto',
          }}
        >
          <Text color={colors.brand.white} fontSize={14} fontWeight={700} lineHeight={17.5}>
            {adjustGeopinToast?.headerText}
          </Text>
          {adjustGeopinToast?.bodyText?.length > 0 ? (
            <Text color={colors.brand.white} fontSize={14} fontWeight={700} lineHeight={17.5}>
              {adjustGeopinToast?.bodyText}
            </Text>
          ) : null}
        </VStack>
        <Box ml={2}>
          <Pressable onPress={handleToggleMapBanner}>
            <CloseIconWhiteSVG />
          </Pressable>
        </Box>
      </HStack>
    )
  }, [handleToggleMapBanner, adjustGeopinToast, calculateOffset, useUpdateNormalizedAddress])

  const MapFooter = useCallback(() => {
    return showGeocodeCorrection && adjustGeopin ? (
      <AdjustGeopin
        key={`adjust-geopin-${adjustGeopin}`}
        order={order}
        adjustGeopin={adjustGeopin}
        mapRef={mapRef}
        toggleEditAddress={handleToggleEditAddress}
        initialAddressCoords={initialAddressCoords}
        addressMarkerCoords={addressMarkerCoords}
        currentAddress={order?.address}
        reroutableZipCodes={order?.reroutableZipCodes}
        resetAddressMarker={resetAddressMarker}
        setAdjustGeopin={setAdjustGeopin}
        handleCloseMap={handleCloseMap}
        handleSetOrder={handleSetOrder}
        mapZoom={mapZoom}
        handleToggleMapBanner={handleToggleMapBanner}
        useUpdateNormalizedAddress={useUpdateNormalizedAddress}
        acceptedTermsAndConditions={acceptedTermsAndConditions}
      />
    ) : (
      <Box
        style={{
          position: 'absolute',
          bottom: isMobile ? 20 : 10,
          width: isMobileScreen ? (isMobile ? '85%' : '50%') : '50%',
          marginLeft: 0,
          right: isMobile ? '7.5%' : '25%',
          zIndex: 3000,
        }}
      >
        <DeliveryAddress
          order={order}
          addInstructions={addInstructions}
          editAddress={editAddress}
          deliveryInstructionsBtnStyleProps={{
            borderRadius: 50,
            backgroundColor: colors.brand.lightGrey,
            marginTop: 3,
          }}
          instructionsRenderContainerStyleProps={{ marginBottom: fullScreenMap ? '15px' : 0 }}
          showViewPackageLocationButton={false}
          showViewAddressButton={false}
          hasProofOfServicePhotosWithLocation={hasProofOfServicePhotosWithLocation}
        />
      </Box>
    )
  }, [
    fullScreenMap,
    order,
    addInstructions,
    editAddress,
    isMobileScreen,
    showGeocodeCorrection,
    adjustGeopin,
    addressMarkerCoords,
    handleCloseMap,
    handleSetOrder,
    handleToggleEditAddress,
    handleToggleMapBanner,
    initialAddressCoords,
    resetAddressMarker,
    mapZoom,
    useUpdateNormalizedAddress,
    acceptedTermsAndConditions,
    hasProofOfServicePhotosWithLocation,
  ])

  // Derive MapPin[] from the mapMarkers state (already computed in the effect
  // above) for the shared component's camera-fitting logic.
  // Children (MapMarkers/AddressMarker) handle the actual pin rendering.
  const pins: MapPin[] = useMemo(
    () =>
      mapMarkers.map(m => ({
        key: m.key ?? `pin-${m.coordinate.latitude}-${m.coordinate.longitude}`,
        coordinate: { lat: m.coordinate.latitude, lng: m.coordinate.longitude },
        variant: (m.variant as MapPin['variant']) ?? 'house',
        zIndex: m.zIndex,
      })),
    [mapMarkers]
  )

  const mapStyleUrl = `https://maps.geo.${mapRegion}.amazonaws.com/maps/v0/maps/${mapName}/style-descriptor?key=${mapKey}`

  return (
    <Box
      style={{
        flex: 1,
        width: isMobileScreen ? '120%' : '100%',
        height: 228,
        ...orderMapStyles?.orderMapContainer,
      }}
    >
      <Box
        style={{
          height: 280,
          borderRadius: 10,
          overflow: 'hidden',
          ...orderMapStyles?.mapContainer,
        }}
      >
        <SharedOrderMap
          pins={pins}
          mapStyleUrl={mapStyleUrl}
          fullScreenMap={fullScreenMap}
          viewRatio={viewRatio}
          bottomPaddingRatio={bottomPaddingRatio}
          onPress={handleMapOnClick}
          mapRef={mapRef}
          onMove={handleMapMove}
        >
          <MapMarkers />
          {userAuthed ? hasValidAddressCoords && <AddressMarker /> : null}
        </SharedOrderMap>
      </Box>
      {fullScreenMap && (
        <Box style={{ position: 'absolute', top: 10, right: 10, zIndex: 3000 }}>
          <Pressable onPress={() => handleCloseMap({ showToastAfterClose: false })}>
            <CloseButtonSVG />
          </Pressable>
        </Box>
      )}
      {fullScreenMap && showBanner && <MapBanner />}
      {fullScreenMap && <MapFooter />}
    </Box>
  )
}

// Extract placement photo from address
const getPlacementPhoto = (address: Address) => {
  const addressImages: Record<string, AddressImage> | undefined = address.images?.reduce((addrImages, image) => {
    return { ...addrImages, [image?.imageType ?? '']: image }
  }, {})
  return addressImages?.[AddressImageType.PlacementPhoto]
}

// Build all marker props for rendering later
const useBuildMarkerPropsArray = ({
  order,
  placementPhoto,
  proofOfServicePhotos,
  userAuthed = false,
}: {
  order: Order
  placementPhoto?: AddressImage
  proofOfServicePhotos?: ProofOfServicePhoto[]
  userAuthed?: boolean
}) => {
  const orderAddress = order?.address
  const { lat: addressLat, lng: addressLng } = orderAddress?.location ?? {}
  let markers: IMapMarkerProps[] = []

  if (addressLat && addressLng) {
    const adddressMarker = {
      key: `marker_address_${orderAddress?.addressId}`,
      coordinate: { latitude: addressLat, longitude: addressLng },
      zIndex: 10,
      variant: (orderAddress?.type?.toLowerCase() as MarkerVariants) ?? 'house',
    } as IMapMarkerProps

    markers.push(adddressMarker)
  }

  if (placementPhoto?.location?.lat && placementPhoto?.location?.lng) {
    const placementPhotoMarker = {
      key: `marker_addressImage_${placementPhoto.imageType}`,
      coordinate: { latitude: placementPhoto.location?.lat, longitude: placementPhoto.location?.lng },
      zIndex: 20,
      variant: 'photo',
      iconBackgroundImage: placementPhoto?.imageUrl ?? undefined,
    } as IMapMarkerProps

    markers.push(placementPhotoMarker)
  }

  if (proofOfServicePhotos?.length && userAuthed) {
    const podMarkers = proofOfServicePhotos
      ?.filter(({ location }) => location?.lat && location?.lng)
      ?.map(({ location, url }: ProofOfServicePhoto, idx) => {
        return {
          key: `marker_proofOfServicePhoto_${idx}`,
          coordinate: { latitude: location!.lat as number, longitude: location!.lng as number },
          zIndex: 4000,
          variant: 'package',
          iconBackgroundImage: url ?? undefined,
        }
      })

    markers = [...markers, ...podMarkers]
  }

  return markers
}

const embeddedKey = import.meta.env.REACT_APP_SMARTY_EMBEDDED_KEY

const getRegionFromAddress = async (address: Address) => {
  try {
    const endpoint = 'https://us-zipcode.api.smarty.com/lookup'
    const params = [`key=${embeddedKey}`, 'max_results=1']
    let coordinateDelta = 1
    if (address?.zipCode) {
      params.push(`zipcode=${address.zipCode}`)
      coordinateDelta = MAP_ZIP_DELTA
    } else if (address?.state && address?.city) {
      params.push(`state=${address.state}`)
      params.push(`city=${address.city}`)
      coordinateDelta = MAP_CITY_DELTA
    } else {
      return [undefined, undefined, undefined, undefined]
    }
    const smartyResponseData = await (
      await fetch(`${endpoint}?${params.join('&')}`, {
        headers: { Referer: 'www.shipveho.com' },
      })
    ).json()
    const zipCodeResult = smartyResponseData?.[0]?.zipcodes?.[0]
    return zipCodeResult?.latitude && zipCodeResult?.longitude
      ? [zipCodeResult.latitude, zipCodeResult.longitude, coordinateDelta, coordinateDelta]
      : [undefined, undefined, undefined, undefined]
  } catch (err) {
    logError('Error fetching address region data', { error: err })
    return [undefined, undefined, undefined, undefined]
  }
}
